import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Logo from '../common/Logo';
import '../salon/styles/base.css'; // Import global form styles
import './Auth.css';

const ResetPassword = () => {
  const { t, translateMessage } = useLanguage();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [token, setToken] = useState('');
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError(t('auth.invalidResetLinkDesc'));
    }
  }, [searchParams, t]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      setLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError(t('auth.passwordMinLength'));
      setLoading(false);
      return;
    }

    const result = await resetPassword(token, formData.password);
    
    if (result.success) {
      setSuccess(translateMessage(result.message));
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } else {
      setError(translateMessage(result.error));
    }
    
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <Logo />
            <p>{t('auth.invalidResetLink')}</p>
          </div>
          <div className="error-message">
            {error}
          </div>
          <div className="auth-links">
            <Link to="/forgot-password" className="link">
              {t('auth.requestNewResetLink')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <Logo />
          <p>{t('auth.setNewPassword')}</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
            <p>{t('auth.redirectingToLogin')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="base-form">
          <div className="form-group">
            <label htmlFor="password">{t('auth.newPassword')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder={t('auth.enterNewPassword')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.confirmNewPassword')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder={t('auth.confirmNewPasswordPlaceholder')}
            />
          </div>

          <button 
            type="submit" 
            className="salon-button primary"
            disabled={loading}
          >
            {loading ? t('auth.resetting') : t('auth.resetPassword')}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/login" className="link">
            {t('auth.backToSignIn')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
